import jwt_decode from "jwt-decode";
import { useCallback } from "react";
import { useHistory } from "react-router-dom";
import { refreshIdentity } from "../../api";
import { LOGIN_STATE_SENTINEL, RETURN_PATH_KEY } from "../../constants";
import { ORGANIZATION_KEY } from "../../contexts";
import { checkStatus } from "../../fetch";
import { AuthType, UserData } from "../../types";
import { recoverBrowserSecurityError } from "../../utils/storage";
import * as Sentry from "@sentry/react";
import { datadogRum } from "@datadog/browser-rum";
import { SESSION_CONTENTS } from "../editor/constants";

const ACCESS_TOKEN_KEY = "access_token";
const LAST_LOGIN_AT_KEY = "last_login_at";
const EXPIRES_AT_KEY = "expires_at";

interface IAuthenticateRequest {
  redirectUri: string;
  code: string;
  state: string | null;
}

interface AuthHeaders {
  Authorization: string;
}

export enum AuthStates {
  Unauthorized = "UNAUTHORIZED",
  Authorized = "AUTHORIZED",
}

const CONTENT_TYPE_JSON = { "Content-Type": "application/json" };

function clearTokens(all?: boolean) {
  recoverBrowserSecurityError(() => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(EXPIRES_AT_KEY);
    if (all) {
      localStorage.removeItem(ORGANIZATION_KEY);
      localStorage.removeItem(LAST_LOGIN_AT_KEY);
      localStorage.removeItem(LOGIN_STATE_SENTINEL);
      localStorage.removeItem(SESSION_CONTENTS);
    }
  });
}

class ApiError extends Error {
  constructor(public statusCode: number, public message: string) {
    super(message);
  }
}

function checkAuthorized(response: Response): Response {
  if (response.status === 401 || response.status === 422) {
    clearTokens();
    const err = new ApiError(
      response.status,
      `Authorization failed: ${response.statusText}`
    );
    Sentry.captureException(err);
    datadogRum.addError(err);
    // reload once sentry event has been sent
    Sentry.close(3000).then(function () {
      window.location.reload();
    });
    throw err;
  } else if (response.status === 403) {
    // user passed a token but isn't allowed to login
    const err = new ApiError(
      response.status,
      `Authorization failed: ${response.statusText}`
    );
    throw err;
  } else {
    return response;
  }
}

function codeToBearer(
  loginInfo: IAuthenticateRequest,
  authProviderId: string = "github"
): Promise<string> {
  const code = loginInfo.code;
  const state = loginInfo.state;
  const redirectUri = loginInfo.redirectUri;
  return fetch("/api/auth/authenticate", {
    method: "post",
    headers: {
      Accept: "application/json, */*",
      "Content-Type": "application/json",
    },
    credentials: "same-origin",
    body: JSON.stringify({
      code,
      state,
      redirectUri,
      authProviderId,
    }),
  })
    .then(checkAuthorized)
    .then((res) => res.json())
    .then((data) => data.token);
}

export function isAuthenticated(): boolean {
  const expiresAt = JSON.parse(
    recoverBrowserSecurityError(() => localStorage.getItem(EXPIRES_AT_KEY)) ||
      "0"
  );
  return new Date().getTime() < expiresAt;
}

export function everAuthenticated(): boolean {
  return (
    recoverBrowserSecurityError(() =>
      localStorage.getItem(LAST_LOGIN_AT_KEY)
    ) !== null
  );
}

function getAccessToken(): string {
  const accessToken = recoverBrowserSecurityError(() =>
    localStorage.getItem(ACCESS_TOKEN_KEY)
  );
  if (!accessToken) {
    throw new Error("No access token found");
  }
  return accessToken;
}

function buildAuthorizationHeader(): AuthHeaders {
  return {
    Authorization: `Bearer ${getAccessToken()}`,
  };
}

export function authRequestRaw<B>(
  method: string,
  path: string,
  body?: B,
  headers?: HeadersInit
): Promise<Response> {
  const hasBody = body !== undefined;
  const sendHeaders: HeadersInit = {
    Accept: "application/json",
    ...headers,
    ...(isAuthenticated() ? buildAuthorizationHeader() : {}),
    ...(hasBody ? CONTENT_TYPE_JSON : {}),
  };

  return fetch(path, {
    method: method,
    headers: sendHeaders,
    credentials: "same-origin",
    ...(hasBody ? { body: JSON.stringify(body) } : {}),
  }).then(checkAuthorized);
}

export function authGet<R>(path: string): Promise<R> {
  return authRequestRaw("get", path)
    .then(checkStatus)
    .then((res) => res.json());
}

export function authGetText(path: string): Promise<string> {
  return authRequestRaw("get", path)
    .then(checkStatus)
    .then((res) => res.text());
}

export function authPatch<B, R>(path: string, body: B): Promise<R> {
  return authRequestRaw<B>("PATCH", path, body)
    .then(checkStatus)
    .then((res) => res.json());
}

export function authPost<B, R>(path: string, body: B): Promise<R> {
  return authRequestRaw<B>("post", path, body)
    .then(checkStatus)
    .then((res) => res.json());
}

export function authPut<B, R>(path: string, body: B): Promise<R> {
  return authRequestRaw<B>("put", path, body)
    .then(checkStatus)
    .then((res) => res.json());
}

export function authDelete<R>(path: string): Promise<R> {
  return authRequestRaw("delete", path)
    .then(checkStatus)
    .then((res) => res.json());
}

const addReturnPathToUrl = (
  targetUrlString: string,
  preferredReturnPath?: string
): string => {
  const currentUrl = new URL(window.location.toString());
  const targetUrl = new URL(targetUrlString, window.location.origin);
  const newReturnPath =
    // reuse existing return path if it exists
    currentUrl.searchParams.get(RETURN_PATH_KEY) ??
    // use requested return path if it exists
    preferredReturnPath ??
    // use current path otherwise
    `${currentUrl.pathname}${currentUrl.search}`;
  targetUrl.searchParams.set(RETURN_PATH_KEY, newReturnPath);
  return `${targetUrl.pathname}${targetUrl.search}`;
};

export const useAuth = () => {
  const history = useHistory();

  const login = useCallback(
    async (
      authProviderId: string,
      authProviderType: AuthType,
      preferredReturnPath?: string
    ) => {
      if (authProviderType === "saml2") {
        const targetPath = addReturnPathToUrl(
          `/api/auth/saml/login/${authProviderId}`,
          preferredReturnPath
        );
        window.location.href = targetPath; // nosemgrep: should-redirect-with-react-router because the SAML redirect URL doesn't exist in the frontend app
      } else {
        const targetPath = addReturnPathToUrl(
          `/oauth/login/${authProviderId}`,
          preferredReturnPath
        );
        history.push(targetPath);
      }
    },
    [history]
  );

  const install = useCallback(
    async (authProviderType: AuthType, preferredReturnPath?: string) => {
      // Allows us to fall back to using the newest org when this function returns
      recoverBrowserSecurityError(() =>
        localStorage.removeItem(ORGANIZATION_KEY)
      );
      const targetPath =
        authProviderType && authProviderType === "gitlab"
          ? "/gitlab/organizations"
          : "/github/install";
      const targetUrl = addReturnPathToUrl(targetPath, preferredReturnPath);
      history.push(targetUrl);
    },
    [history]
  );

  const logout = useCallback(async () => {
    // Allows us to fall back to using the newest org when this function returns
    clearTokens();
    // refresh page so user changes are reflected
    history.go(0);
  }, [history]);

  const logoutAll = useCallback(async () => {
    // Allows us to fall back to using the newest org when this function returns
    clearTokens(true);
    // refresh page so user changes are reflected
    history.go(0);
  }, [history]);

  return { login, logout, logoutAll, install };
};

export const logoutBack = () => {
  clearTokens();
  window.history.back();
};

export const finishUserAuth = async (
  redirectUri: string,
  code: string,
  state: string | null,
  authProviderId: string = "github"
): Promise<void> => {
  // post code to backend and do auth there;
  const token = await codeToBearer(
    { redirectUri, code, state },
    authProviderId
  );

  // save bearer token from backend to localStorage
  const decoded = jwt_decode<any>(token);
  recoverBrowserSecurityError(() => {
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
    localStorage.setItem(LAST_LOGIN_AT_KEY, new Date().getTime().toString());
    localStorage.setItem(EXPIRES_AT_KEY, JSON.stringify(decoded.exp * 1000));
  });
};

export const finishSamlAuth = async (token: string): Promise<void> => {
  // TODO drew switch to single-use code in the url param

  // save bearer token from backend to localStorage
  const decoded = jwt_decode<any>(token);
  recoverBrowserSecurityError(() => {
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
    localStorage.setItem(LAST_LOGIN_AT_KEY, new Date().getTime().toString());
    localStorage.setItem(EXPIRES_AT_KEY, JSON.stringify(decoded.exp * 1000));
  });
};

export const refreshUserAuth = async (): Promise<void> => {
  // post code to backend and do auth there;
  const { token } = await refreshIdentity();

  // save bearer token from backend to localStorage
  const decoded = jwt_decode<any>(token);
  recoverBrowserSecurityError(() => {
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
    localStorage.setItem(EXPIRES_AT_KEY, JSON.stringify(decoded.exp * 1000));
  });
};

export const getUserData = (): UserData => {
  const token = getAccessToken();
  const { identity } = jwt_decode<any>(token);
  return identity;
};

export const getAuthenticationState = (): AuthStates => {
  // All users must be properly authenticated and have a valid user id.
  if (isAuthenticated()) {
    return AuthStates.Authorized;
  } else {
    return AuthStates.Unauthorized;
  }
};
